import type { SettingGroupName, TestConnectionTarget } from '@ytclip/shared'
import { DESKTOP_APP_SETTING_GROUPS } from '@ytclip/shared'
import { Save } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { Tabs, TabPanel } from '@/components/ui/tabs.tsx'
import { isDirty, useSettingsDraft } from '@/features/admin/use-settings-draft.ts'
import { GroupFields } from './setting-control.tsx'

const TESTABLE: Partial<Record<string, TestConnectionTarget>> = {
  litellm: 'litellm',
  doit: 'doit',
  smtp: 'smtp',
}

/** The desktop-app groups have their own console at `/admin/desktop-app`. */
function isGeneralGroup(group: SettingGroupName): boolean {
  return !DESKTOP_APP_SETTING_GROUPS.includes(group)
}

export function AdminSettingsPage() {
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const settings = useSettingsDraft(useCallback(isGeneralGroup, []))

  const active = settings.groups.find((group) => group.group === activeGroup) ?? settings.groups[0]

  const tabs = settings.groups.map((group) => ({
    id: group.group,
    label: group.label,
    // Surfaces edits left behind on a tab the admin has navigated away from.
    badge: group.settings.filter((setting) => isDirty(setting, settings.draft)).length,
  }))

  return (
    <>
      <PageHeader
        title="Pengaturan"
        description="Kredensial dan angka bisnis. Perubahan langsung berlaku tanpa deploy ulang."
        action={
          <Button
            disabled={settings.changed.length === 0}
            loading={settings.saving}
            onClick={settings.save}
            icon={<Save className="size-4" />}
          >
            Simpan{settings.changed.length > 0 ? ` (${settings.changed.length})` : ''}
          </Button>
        }
      />

      {settings.isPending ? (
        <LoadingBlock />
      ) : settings.isError || !active ? (
        <ErrorState message="Pengaturan tidak bisa dimuat." />
      ) : (
        <Card>
          <CardBody className="pt-2">
            <Tabs
              label="Kelompok pengaturan"
              items={tabs}
              activeId={active.group}
              onChange={setActiveGroup}
            />

            <TabPanel id={active.group}>
              <GroupFields
                settings={active.settings}
                target={TESTABLE[active.group]}
                draft={settings.draft}
                onChange={settings.setValue}
              />
            </TabPanel>
          </CardBody>
        </Card>
      )}
    </>
  )
}
